<template>
    <div>
        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Policy</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.client_company_id"
                                :items="clients"
                                class="required"
                                label="Client"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                                :rules="[v => ruleRequired(v, 'Client')]"
                                :disabled="submission.creation_method === 'automated'"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-text-field
                                v-model="submission.client_reference"
                                label="Client reference (if applicable)"
                                placeholder=" "
                                outline
                                maxlength="50"
                                :disabled="submission.creation_method === 'automated'"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex>
                            <x-radio-group
                                v-model="isKnownFacility"
                                :row="true"
                                @change="emitIsKnownFacility"
                            >
                                <template v-slot:label>
                                    Facility information known?
                                </template>

                                <x-radio label="Yes" :value="true" />

                                <x-radio label="No" :value="false" />
                            </x-radio-group>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-select
                                v-model="submission.facility_id"
                                :items="filteredFacilities"
                                label="Facility"
                                :class="{ required: isKnownFacility }"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                                :rules="[v => ruleRequiredIf(v, isKnownFacility, true, 'Facility')]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.placing_broker_contact_id"
                                :items="filteredClientAssignments"
                                label="Placing broker contact"
                                placeholder=" "
                                item-value="broker_user_id"
                                item-text="user_name"
                                append-icon="keyboard_arrow_down"
                                :menu-props="{ offsetY: true }"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.entities"
                                :items="filteredEntities"
                                label="Entities"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                                multiple
                            >
                                <template slot="selection" slot-scope="{ item, index }">
                                    <span v-if="submission.entities.length === 1">
                                        {{ item.name }}
                                    </span>

                                    <span v-else-if="submission.entities.length > 1">
                                        {{ submission.entities.length }} entities selected
                                    </span>
                                </template>
                            </x-autocomplete>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.category_id"
                                :items="filteredCategories"
                                label="Category"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.product_id"
                                :items="filteredProducts"
                                label="Product"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="this.selectedCategory?.name === 'Loans'" xs6>
                        <x-flex xs6>
                            <x-text-field
                                v-model="submission.loan_radar_id"
                                label="Loan radar ID (if applicable)"
                                placeholder=" "
                            />
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-flex>
        </x-layout>

        <x-layout v-show="!hasNoObligor" class="question-set guarantors">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Obligors</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs12>
                            <x-radio-group
                                v-model="showObligorFields"
                                :row="true"
                            >
                                <template v-slot:label>
                                    Add obligor?
                                </template>

                                <x-radio label="Yes" :value="true" />

                                <x-radio label="No" :value="false" />
                            </x-radio-group>
                        </x-flex>
                    </x-layout>

                    <template v-if="showObligorFields">
                        <x-layout v-for="(obligor, index) in submission.obligors" :key="'obligor-' + index">
                            <x-flex>
                                <x-autocomplete
                                    v-model="obligor.country_id"
                                    :items="countries"
                                    class="required"
                                    label="Obligor country"
                                    placeholder=" "
                                    item-value="id"
                                    item-text="name"
                                    append-icon="keyboard_arrow_down"
                                    :rules="[
                                    v => ruleRequired(v, 'Obligor country')
                                ]"
                                />

                                <x-text-field
                                    v-if="submission.creation_method === 'automated' && obligor.id"
                                    v-model="obligor.source_country"
                                    placeholder=" "
                                    outline
                                    :disabled="true"
                                />
                            </x-flex>

                            <x-flex>
                                <x-autocomplete
                                    v-model="obligor.obligor_id"
                                    :items="filteredObligors[index]"
                                    class="required"
                                    label="Obligor"
                                    placeholder=" "
                                    item-value="id"
                                    item-text="name"
                                    append-icon="keyboard_arrow_down"
                                    :rules="[
                                    v => ruleRequired(v, 'Obligor')
                                ]"
                                />

                                <x-text-field
                                    v-if="submission.creation_method === 'automated' && obligor.id"
                                    v-model="obligor.source_name"
                                    placeholder=" "
                                    outline
                                    :disabled="true"
                                />
                            </x-flex>

                            <x-flex class="grow-0">
                                <x-btn
                                    class="square-icon label-spacing"
                                    color="tertiary"
                                    small
                                    :disabled="submission.obligors.length === 1"
                                    @click="removeObligorSection(index)"
                                >
                                    <i class="material-icons-outlined">delete_forever</i>
                                </x-btn>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex>
                                <x-btn
                                    color="tertiary"
                                    small
                                    block
                                    @click="addObligorSection"
                                >
                                    <i class="material-icons mr-2">add</i>

                                    Add another Obligor
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <x-layout v-show="!hasNoObligor" class="question-set guarantors">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Guarantors</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs12>
                            <x-radio-group
                                v-model="showGuarantorFields"
                                :row="true"
                            >
                                <template v-slot:label>
                                    Add guarantor?
                                </template>

                                <x-radio label="Yes" :value="true" />

                                <x-radio label="No" :value="false" />
                            </x-radio-group>
                        </x-flex>
                    </x-layout>

                    <template v-if="showGuarantorFields">
                        <x-layout v-for="(guarantor, index) in submission.guarantors" :key="'guarantor-' + index">
                            <x-flex>
                                <x-autocomplete
                                    v-model="guarantor.country_id"
                                    :items="countries"
                                    label="Guarantor country"
                                    class="required"
                                    placeholder=" "
                                    item-value="id"
                                    item-text="name"
                                    append-icon="keyboard_arrow_down"
                                    :rules="[
                                        v => ruleRequired(v, 'Guarantor country')
                                    ]"
                                />

                                <x-text-field
                                    v-if="submission.creation_method === 'automated' && guarantor.id"
                                    v-model="guarantor.source_country"
                                    placeholder=" "
                                    outline
                                    :disabled="true"
                                />
                            </x-flex>

                            <x-flex>
                                <x-autocomplete
                                    v-model="guarantor.guarantor_id"
                                    :items="filteredGuarantors[index]"
                                    label="Guarantor"
                                    class="required"
                                    placeholder=" "
                                    item-value="id"
                                    item-text="name"
                                    append-icon="keyboard_arrow_down"
                                    :rules="[
                                        v => ruleRequired(v, 'Guarantor')
                                    ]"
                                />

                                <x-text-field
                                    v-if="submission.creation_method === 'automated' && guarantor.id"
                                    v-model="guarantor.source_name"
                                    placeholder=" "
                                    outline
                                    :disabled="true"
                                />
                            </x-flex>

                            <x-flex class="grow-0">
                                <x-btn
                                    class="square-icon label-spacing"
                                    color="tertiary"
                                    small
                                    :disabled="submission.guarantors.length === 1"
                                    @click="removeGuarantorSection(index)"
                                >
                                    <i class="material-icons-outlined">delete_forever</i>
                                </x-btn>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex>
                                <x-btn
                                    color="tertiary"
                                    small
                                    block
                                    @click="addGuarantorSection"
                                >
                                    <i class="material-icons mr-2">add</i>

                                    Add another guarantor
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </template>
                </x-container>
            </x-flex>
        </x-layout>


        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Product</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="submission.currency_id"
                                :items="currencies"
                                class="required"
                                label="Currency"
                                placeholder=" "
                                item-value="id"
                                item-text="name"
                                append-icon="keyboard_arrow_down"
                                :menu-props="{ offsetY: true }"
                                :rules="[v => ruleRequired(v, 'Currency')]"
                            />
                        </x-flex>
                        <x-flex xs6 v-if="productHasField('business_line')">
                            <x-select
                                v-model="submission.business_line"
                                :items="businessLines"
                                label="Business line"
                                placeholder=" "
                                append-icon="keyboard_arrow_down"
                                outline
                                :menu-props="{
                                    offsetY: true
                                }"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.total_facility_amount"
                                label="Total facility amount"
                                class="required"
                                placeholder=" "
                                :prefix="currencyCode"
                                optional
                                :rules="[
                                    v => ruleRequired(v, 'Total facility amount'),
                                    v => ruleGreaterThan(v, 0, 'Total facility amount')
                                ]"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.limit_of_liability"
                                label="Limit of liability"
                                class="required"
                                placeholder=" "
                                :prefix="currencyCode"
                                optional
                                :rules="[
                                    v => ruleRequired(v, 'Limit of liability'),
                                    v => ruleNumeric(v, false, false, true),
                                    v => ruleGreaterThan(v, 0, 'Limit of liability'),
                                    v => ruleLessThanOrEqualTo(v, submission.total_facility_amount, 'Limit of liability')
                                ]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6 class="d-flex">
                            <x-text-field
                                v-model="totalRiskPeriodYears"
                                class="risk-period-input required"
                                label="Total risk period"
                                placeholder=" "
                                :suffix="totalRiskPeriodYears === 1 ? 'year' : 'years'"
                                :rules="[
                                    v => ruleRequired(v, 'Total years'),
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Total years')
                                ]"
                            />

                            <span class="risk-period-divider">/</span>

                            <x-text-field
                                v-model="totalRiskPeriodDays"
                                class="risk-period-input"
                                label=" "
                                :suffix="totalRiskPeriodDays === 1 ? 'day' : 'days'"
                                :rules="[
                                    v => ruleRequired(v, 'Total days'),
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Total days'),
                                    v => ruleLessThan(v, 365, 'Total days')
                                ]"
                            />
                        </x-flex>

                        <x-flex xs6 v-if="productHasField('repayment_structure')">
                            <x-text-field
                                v-model="submission.repayment_structure"
                                label="Repayment structure"
                                placeholder=" "
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="productHasField('purpose')">
                        <x-flex xs6>
                            <x-text-field
                                v-model="submission.utilisation_purpose"
                                label="Utilisation purpose"
                                placeholder=" "
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.gross_margin"
                                label="Gross margin"
                                placeholder=" "
                                suffix="BPS"
                                optional
                                :rules="[
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Gross margin'),
                                ]"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.net_margin"
                                label="Net margin"
                                placeholder=" "
                                suffix="BPS"
                                optional
                                :rules="[
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Net margin'),
                                ]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.estimated_revenue_this_year"
                                label="Estimated revenue (this financial year)"
                                placeholder=" "
                                :prefix="currencyCode"
                                optional
                                :rules="[
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Estimated revenue (this financial year)'),
                                ]"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.estimated_revenue_policy_life"
                                label="Estimated revenue (policy life)"
                                placeholder=" "
                                :prefix="currencyCode"
                                optional
                                :rules="[
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Estimated revenue (policy life)'),
                                ]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <x-radio-group
                                v-model="submission.is_secured"
                                :row="true"
                            >
                                <template v-slot:label>
                                    Secured?
                                </template>

                                <x-radio label="Yes" :value="true" />

                                <x-radio label="No" :value="false" />
                            </x-radio-group>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-menu
                                v-model="dateEnquiryInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="submission.date_enquiry_received"
                                        label="Date enquiry received"
                                        placeholder=" "
                                        append-icon="event"
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="submission.date_enquiry_received"
                                    @input="dateEnquiryInput = false"
                                />
                            </x-menu>
                        </x-flex>

                        <x-flex xs6>
                            <x-menu
                                v-model="anticipatedBindDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="submission.anticipated_bind_date"
                                        label="Anticipated bind date"
                                        placeholder=" "
                                        append-icon="event"
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="submission.anticipated_bind_date"
                                    @input="anticipatedBindDateInput = false"
                                />
                            </x-menu>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-menu
                                v-model="inceptionDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="submission.inception_date"
                                        label="Start date"
                                        placeholder=" "
                                        append-icon="event"
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="submission.inception_date"
                                    @input="inceptionDateInput = false"
                                    :max="submission.expiry_date"
                                />
                            </x-menu>
                        </x-flex>

                        <x-flex xs6>
                            <x-menu
                                v-model="expiryDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="submission.expiry_date"
                                        label="End date"
                                        placeholder=" "
                                        append-icon="event"
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="submission.expiry_date"
                                    @input="expiryDateInput = false"
                                    :min="submission.inception_date"
                                />
                            </x-menu>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-flex>
        </x-layout>

        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Additional information</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs12>
                            <x-textarea
                                v-model="submission.additional_information"
                                label="Additional information"
                                placeholder=" "
                                rows="16"
                                :readonly="submission.creation_method === 'automated'"
                            />
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-flex>
        </x-layout>
    </div>
</template>

<script>
import Vue from "vue";
import rules from "../../../mixins/rules";
import EntityField from "../UIComponents/EntityField.vue";
import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

export default {
    name: 'MSTEntryClientInformation',
    components: {EntityField},
    mixins: [rules, submissionQuoteHelpers],
    props: {
        submission: {
            type: Object,
            required: true
        },
        companies: {
            type: Array,
            required: true
        },
        facilities: {
            type: Array,
            required: true
        },
        entities: {
            type: Array,
            required: true
        },
        categories: {
            type: Array,
            required: true
        },
        products: {
            type: Array,
            required: true
        },
        countries: {
            type: Array,
            required: true
        },
        obligors: {
            type: Array,
            required: true
        },
        currencies: {
            type: Array,
            required: true
        },
        businessLines: {
            type: Array,
            required: true
        },
        clientAssignments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            nextKey: 1,
            showObligorFields: false,
            showGuarantorFields: false,
            anticipatedBindDateInput: false,
            dateEnquiryInput: false,
            inceptionDateInput: false,
            expiryDateInput: false,
            isKnownFacility: undefined,
        };
    },
    methods: {
        resetObligors() {
            this.submission.obligors = [];
        },
        resetGuarantors() {
            this.submission.guarantors = [];
        },
        emitIsKnownFacility() {
            this.$emit('update:isKnownFacility', this.isKnownFacility);
        },
        userCanAccessClient(clientCompanyId) {
            const assignments = this.clientAssignments.filter(assignment => assignment.client_company_id === clientCompanyId);

            return assignments.length === 0 ||
                assignments.some(assignment => assignment.broker_user_id === this.$root.user?.id);
        },
    },
    mounted() {
        this.isKnownFacility = this.submission.is_known_facility;
        this.emitIsKnownFacility();

        this.showObligorFields = this.submission.obligors.length > 0;
        this.showGuarantorFields = this.submission.guarantors.length > 0;
    },
    computed: {
        clients() {
            return (this.companies ?? [])
                .filter(company => company.company_type === 'client')
                .filter(company => company.id === this.submission.client_company_id || this.userCanAccessClient(company.id));
        },
        filteredClientAssignments() {
            return this.clientAssignments.filter(broker => broker.client_company.id === this.submission.client_company_id);
        },
    },
    watch: {
        showObligorFields(value) {
            if (!value) {
                this.resetObligors();
            }
        },
        showGuarantorFields(value) {
            if (!value) {
                this.resetGuarantors();
            }
        },
        'submission.facility_id'(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.submission.entities = [];
                if (!(this.filteredCategories ?? []).some(({id}) => this.submission.category_id === id)) {
                    Vue.set(this.submission, 'category_id', null);
                }
            }
        },
        'submission.category_id'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (!(this.filteredProducts ?? []).some(({id}) => this.submission.product_id === id)) {
                    Vue.set(this.submission, 'product_id', null);
                }
            }
        },
        'submission.product_id'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (!this.productHasField('business_line')) {
                    Vue.set(this.submission, 'business_line', null);
                }
                if (!this.productHasField('repayment_structure')) {
                    Vue.set(this.submission, 'repayment_structure', null);
                }
                if (!this.productHasField('purpose')) {
                    Vue.set(this.submission, 'utilisation_purpose', null);
                }
            }
        },
        'submission.client_company_id'(newVal, oldVal) {
            if (newVal !== oldVal) {
                Vue.set(this.submission, 'facility_id', null);
                Vue.set(this.submission, 'placing_broker_contact_id', null);
            }
        }
    }
}
</script>

<style scoped lang="scss">
.risk-period-input {
    width: 50%;
}

.risk-period-divider {
    width: 23px;
    margin: 20px 0 0;
    color: $color-body-text;
    line-height: 40px;
    text-align: center;
}
</style>
